<template>
  <div class="edume-content" :class="additionalStyle">
    <Link :lfe-url="lfeUrl" :app-colour="appColour">
      <template #image>
        <div class="image-background" />
        <img
          v-if="contentType !== 'guide'"
          alt="Content image"
          :src="contentImg"
        />
        <div v-if="contentType === 'guide'" class="image-background">
          <img
            :class="{'guide-thumbnail': contentType === 'guide'}"
            alt="Content image"
            :src="contentImg"
          />
        </div>
      </template>
      <template #title>
        <span :class="titleClass">
          {{ title }}
        </span>
      </template>
      <template v-if="dueDate" #dueDate>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="8"
          viewBox="-0.5 -0.5 9 9"
          fill="none"
        >
          <circle cx="4" cy="4" r="4" fill="#FE535B" />
        </svg>
        {{ dueDateMessage }}
      </template>
      <template #text>
        <span class="edume-content-text-wrapper">
          <span class="edume-content-status">{{ statusMessage }}</span>
          <span class="edume-activities-text">{{ activitiesMessage }}</span>
        </span>
      </template>
    </Link>
  </div>
</template>

<script>
import {getAuthUrl} from '../utils/utils';
import Link from './Link.vue';
import {appColour} from './Widget.vue';
import {polyglot} from '../utils/polyglot';

const DEFAULT_IMAGE_URL =
  'https://edume-res.cloudinary.com/image/upload/w_200/beekeeper_integration/default_course_image.png';

const statusTextKey = {
  NOT_STARTED: 'notStarted',
  IN_PROGRESS: 'inProgress',
};

export default {
  components: {
    Link,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    imageUrl: {
      type: String,
      default: null,
    },
    status: {
      type: String,
      required: true,
    },
    incompleteActivities: {
      type: Number,
      default: 0,
    },
    tenantUrl: {
      type: String,
      required: true,
    },
    parentGroupId: {
      type: Number,
      required: true,
    },
    teamIds: {
      type: Array,
      required: true,
    },
    contentId: {
      type: Number,
      required: true,
    },
    contentType: {
      type: String,
      required: true,
    },
    additionalStyle: {
      type: String,
      default: '',
    },
    isDesktop: {
      type: Boolean,
      required: true,
    },
    dueDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      appColour,
      statusTextKey,
      defaultImageUrl: DEFAULT_IMAGE_URL,
      revisitCoursesMessage: polyglot.t('Courses.revisit'),
    };
  },
  computed: {
    lfeUrl() {
      return getAuthUrl({
        tenantUrl: this.tenantUrl,
        isDesktop: this.isDesktop,
        contentType: this.contentType,
        contentId: this.contentId,
        teamIds: this.teamIds,
        parentGroupId: this.parentGroupId,
      });
    },
    activitiesMessage() {
      const contentPrefix = this.contentType === 'guide' ? 'Guides' : 'Courses';
      const activityTextKey =
        this.incompleteActivities === 1
          ? `${contentPrefix}.activityTodo.single`
          : `${contentPrefix}.activityTodo.plural`;

      return this.incompleteActivities > 0
        ? polyglot.t(`${activityTextKey}`, {
            activityCount: this.incompleteActivities,
          })
        : null;
    },
    contentImg() {
      return this.imageUrl || this.defaultImageUrl;
    },
    statusMessage() {
      return polyglot.t(`CourseStatus.${statusTextKey[this.status]}`);
    },
    dueDateMessage() {
      if (!this.dueDate) {
        return null;
      }
      const dueDateObj = new Date(this.dueDate);
      const today = new Date();
      if (
        dueDateObj.getDate() === today.getDate() &&
        dueDateObj.getMonth() === today.getMonth() &&
        dueDateObj.getFullYear() === today.getFullYear()
      ) {
        return polyglot.t('DueDate.dueToday');
      }
      return polyglot.t('DueDate.dueOn', {
        date: dueDateObj.toLocaleDateString(polyglot.locale, {
          day: 'numeric',
          month: 'short',
        }),
      });
    },
    titleClass() {
      return this.dueDate ? 'truncate' : '';
    },
  },
};
</script>

<style>
.edume-content {
  border-radius: 4px;
  margin: 0 0px 2px;
  box-shadow:
    0px 1px 2px rgba(32, 32, 33, 0.06),
    0px 2px 8px rgba(32, 32, 33, 0.1);
  background-color: white;
}

.first {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  margin-bottom: 2px;
}

.first img {
  border-bottom-left-radius: 0 !important;
}

.middle {
  border-radius: 0;
  margin-bottom: 2px;
}

.middle img {
  border-radius: 0 !important;
}

.last {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  margin-bottom: 2px;
}

.last img {
  border-top-left-radius: 0 !important;
}

.edume-content-text-wrapper {
  display: flex;
  gap: 6px;
}

.edume-content-status {
  display: inline-block;
  font-weight: 600;
}

.edume-activities-text {
  display: inline-block;
  font-weight: 400;
}

.edume-revisit {
  margin-top: 9px;
}

.image-background {
  width: var(--image-size);
  min-height: var(--image-size);
  height: 100%;
  top: 0;
  left: 0;
  position: absolute;
  background-color: #696488;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.first .image-background {
  border-bottom-left-radius: 0 !important;
}

.middle .image-background {
  border-radius: 0 !important;
}

.last .image-background {
  border-top-left-radius: 0 !important;
}

.image-background img {
  margin: 0;
  padding: 0;
}

.truncate {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.guide-thumbnail,
.last .guide-thumbnail,
.middle .guide-thumbnail,
.first .guide-thumbnail {
  width: 32px !important;
  height: 57px !important;
  border-radius: 4px !important;
  object-fit: cover;
  left: 50%;
  top: 50%;
  position: absolute;
  transform: translateX(-50%) translateY(-50%);
}
</style>
